import { util } from "../util/util";
import { FakeLodash } from "../coreutil/FakeLodash";
export var Angles;
(function (Angles) {
    var positive_mod = util.positive_mod;
    function radiansToDegrees(radians) {
        return radians / (2 * Math.PI) * 360;
    }
    Angles.radiansToDegrees = radiansToDegrees;
    function degreesToRadians(degrees) {
        return (degrees / 360) * (2 * Math.PI);
    }
    Angles.degreesToRadians = degreesToRadians;
    Angles.EQUALITY_EPSILON = degreesToRadians(0.01);
    function isSameRadians(a, b) {
        return Math.abs(a - b) < Angles.EQUALITY_EPSILON;
    }
    Angles.isSameRadians = isSameRadians;
    /**
     * Normalizes a radians angle into the [0, 2*PI] range
     * @param radians An angle in radians
     */
    function normalizeAngle(radians) {
        while (radians < 0)
            radians += 2 * Math.PI;
        while (radians > 2 * Math.PI)
            radians -= 2 * Math.PI;
        return radians;
    }
    Angles.normalizeAngle = normalizeAngle;
    function circularMean(angles) {
        return Math.atan2(FakeLodash.sum(angles.map(Math.sin)), FakeLodash.sum(angles.map(Math.cos)));
    }
    Angles.circularMean = circularMean;
    /**
     * Computes an unsigned angle difference in the range [0, PI]
     * @param a The first angle
     * @param b The second angle
     */
    function angleDifference(a, b) {
        return Math.abs(angleDifferenceSigned(a, b));
    }
    Angles.angleDifference = angleDifference;
    /**
     * Computes a signed angle difference in the range [-PI, PI]
     * @param a The first angle
     * @param b The second angle
     */
    function angleDifferenceSigned(a, b) {
        return positive_mod(b - a + Math.PI, 2 * Math.PI) - Math.PI;
    }
    Angles.angleDifferenceSigned = angleDifferenceSigned;
    function toString(self, precision = 1) {
        return `${radiansToDegrees(self).toFixed(precision)}°`;
    }
    Angles.toString = toString;
    let AngleRange;
    (function (AngleRange) {
        function construct(from, to) {
            return normalize({ from, to });
        }
        AngleRange.construct = construct;
        function split(self, sections) {
            const section_size = AngleRange.size(self) / sections;
            return new Array(sections).fill(null).map((_, i) => {
                return {
                    from: self.from + i * section_size,
                    to: self.from + (i + 1) * section_size,
                };
            });
        }
        AngleRange.split = split;
        function contains(range, angle) {
            if (angle >= range.from - Angles.EQUALITY_EPSILON && angle <= range.to + Angles.EQUALITY_EPSILON)
                return true;
            const complement = angle + 2 * Math.PI;
            return complement > range.from && complement < range.to;
        }
        AngleRange.contains = contains;
        function normalize(range) {
            if ([range.from, range.to].every(a => a > 2 * Math.PI)) {
                range.from = normalizeAngle(range.from);
                range.to = normalizeAngle(range.to);
            }
            if (range.to < range.from)
                range.to += 2 * Math.PI;
            return range;
        }
        AngleRange.normalize = normalize;
        function shrink(range, factor) {
            normalize(range);
            const offset = angleDifference(range[0], range[1]) * factor / 2;
            return normalize({
                from: range.from + offset,
                to: range.to - offset
            });
        }
        AngleRange.shrink = shrink;
        function around(angle, size) {
            return normalize({
                from: angle - size / 2,
                to: angle + size / 2,
            });
        }
        AngleRange.around = around;
        function fromAngles(...angles) {
            let mean = circularMean(angles);
            if (mean < 0)
                mean += 2 * Math.PI;
            let range = {
                from: mean,
                to: mean
            };
            for (let angle of angles) {
                if (angleDifference(angle, mean) > Math.PI)
                    angle += 2 * Math.PI;
                if (angle < range.from)
                    range.from = angle;
                if (angle > range.to)
                    range.to = angle;
            }
            return normalize(range);
        }
        AngleRange.fromAngles = fromAngles;
        function mean(self) {
            return normalizeAngle((self.from + self.to) / 2);
        }
        AngleRange.mean = mean;
        function between(a, b) {
            // Special case when a and b are the same infinitely small range
            if (a == b && a.from == a.to)
                return construct(a.to + Angles.EQUALITY_EPSILON, a.from - Angles.EQUALITY_EPSILON);
            return construct(a.to, b.from);
        }
        AngleRange.between = between;
        function merge(a, b) {
            return construct(Math.min(a.from, b.from), Math.max(a.to, b.to));
        }
        AngleRange.merge = merge;
        function size(self) {
            return normalizeAngle(self.to - self.from);
        }
        AngleRange.size = size;
        function overlaps(a, b) {
            return (a.from <= b.to && a.to >= b.from) || (b.from <= a.to && b.to >= a.from);
        }
        AngleRange.overlaps = overlaps;
        function toString(self, precision = 1) {
            return `${Angles.toString(self.from, precision)} - ${Angles.toString(self.to, precision)}`;
        }
        AngleRange.toString = toString;
    })(AngleRange = Angles.AngleRange || (Angles.AngleRange = {}));
    let UncertainAngle;
    (function (UncertainAngle) {
        var positive_mod = util.positive_mod;
        function fromAngle(angle) {
            return {
                median: angle,
                epsilon: 0,
                range: AngleRange.fromAngles(angle)
            };
        }
        UncertainAngle.fromAngle = fromAngle;
        function fromEpsilonAngle(angle, epsilon) {
            return {
                median: angle,
                epsilon: epsilon,
                range: { from: angle - epsilon, to: angle + epsilon }
            };
        }
        UncertainAngle.fromEpsilonAngle = fromEpsilonAngle;
        function fromRange(range) {
            return {
                median: AngleRange.mean(range),
                epsilon: AngleRange.size(range) / 2,
                range: range
            };
        }
        UncertainAngle.fromRange = fromRange;
        function meanDifference(a, b) {
            return Math.abs(positive_mod(a.median - b.median + Math.PI, 2 * Math.PI) - Math.PI);
        }
        UncertainAngle.meanDifference = meanDifference;
        function toAngleString(self, precision = 1) {
            return `${radiansToDegrees(self.median).toFixed(precision)}°`;
        }
        UncertainAngle.toAngleString = toAngleString;
        function toUncertaintyString(self, precision = 2) {
            return `±${radiansToDegrees(self.epsilon).toFixed(precision)}°`;
        }
        UncertainAngle.toUncertaintyString = toUncertaintyString;
        function toString(self, precision = 1) {
            return toAngleString(self, precision) + " " + toUncertaintyString(self, precision);
        }
        UncertainAngle.toString = toString;
        function contains(self, angle) {
            return angleDifference(angle, self.median) < (self.epsilon + Angles.EQUALITY_EPSILON);
        }
        UncertainAngle.contains = contains;
        function equals(a, b) {
            return Angles.isSameRadians(a.median, b.median) && Angles.isSameRadians(a.epsilon, b.epsilon);
        }
        UncertainAngle.equals = equals;
    })(UncertainAngle = Angles.UncertainAngle || (Angles.UncertainAngle = {}));
})(Angles || (Angles = {}));
